import { useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import { useCompleteFragment } from '@eluve/apollo-client';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Icon,
  Input,
  LoadingIndicator,
  Stack,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@eluve/components';
import { useUserIdFromSession } from '@eluve/session-helpers';
import { useDialog } from '@eluve/utility-hooks';

import { getAppointmentTenantInfoQuery } from '../../command-palette/operations';
import { useProvisionAndNavigate } from '../../command-palette/useProvisionAndNavigate';

import { usersTenantsFragment } from './TenantActionsColumn';

export const QuickProvisionDialog = () => {
  const { isDialogOpen, setIsDialogOpen } = useDialog();

  const [appointmentId, setAppointmentId] = useState('');

  const [searchParams, setSearchParams] = useSearchParams();
  const goToAppointmentId = searchParams.get('goToAppointmentId');

  const userId = useUserIdFromSession();

  // Search Param Handling

  useEffect(() => {
    if (goToAppointmentId) {
      setAppointmentId(goToAppointmentId);
      setIsDialogOpen(true);
      setSearchParams((prev) => {
        prev.delete('goToAppointmentId');
        return prev;
      });
    }
  }, [goToAppointmentId, setIsDialogOpen, setSearchParams]);

  // Appointment & Tenant Data

  const usersTenants = useCompleteFragment({
    fragment: usersTenantsFragment,
    key: { id: userId },
    strict: false,
  });

  const isValidUuid = z.string().uuid().safeParse(appointmentId).success;

  const {
    loading: isLoading,
    data: appointmentData,
    error: appointmentError,
  } = useQuery(getAppointmentTenantInfoQuery, {
    variables: { appointmentId },
    skip: !isValidUuid,
  });

  const appointment = appointmentData?.appointments?.[0];
  const tenantId = appointment?.tenantId ?? null;
  const tenantName = appointment?.tenant?.name ?? null;

  const isProvisioned =
    tenantId &&
    (usersTenants?.tenants ?? []).some(
      (tenant) => tenant.tenantId === tenantId,
    );

  const queryErrorMessage =
    appointmentError || (appointmentData && (!tenantId || !tenantName))
      ? 'Appointment details could not be loaded. Please try again!'
      : null;

  // Provisioning

  const {
    run: provisionAndNavigate,
    status: provisionStatus,
    error: provisionError,
    atCapacity: hasReachedTenantLimit,
  } = useProvisionAndNavigate();
  const isProvisioning =
    provisionStatus === 'provisioning' || provisionStatus === 'switching';

  const errorMessage = provisionError ?? queryErrorMessage;

  useEffect(() => {
    // Auto-navigate the moment we have a tenant for an already-provisioned
    // user. `run` short-circuits to navigate when membership is detected,
    // so it's safe to invoke here without re-running the full provision.
    if (isProvisioned && tenantId) {
      void provisionAndNavigate({
        tenantId,
        targetPath: `/admin/organizations/${tenantId}/appointments/${appointmentId}`,
      });
    }
  }, [isProvisioned, tenantId, appointmentId, provisionAndNavigate]);

  // Handlers

  const handleDialogChange = (open: boolean) => {
    if (!isDialogOpen && open) {
      setAppointmentId('');
    }
    setIsDialogOpen(open);
  };

  const handleProvision = async () => {
    if (hasReachedTenantLimit || !tenantId) {
      return;
    }
    await provisionAndNavigate({
      tenantId,
      targetPath: `/admin/organizations/${tenantId}/appointments/${appointmentId}`,
    });
  };

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAppointmentId(e.target.value);
  };

  return (
    <Dialog open={isDialogOpen} onOpenChange={handleDialogChange}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <Icon name="FileEdit" size="auto" />
          Go to Appointment...
        </Button>
      </DialogTrigger>
      <DialogContent size="lg">
        <DialogHeader className="mb-1 space-y-2">
          <DialogTitle>Go to Appointment</DialogTitle>
          <DialogDescription>
            Navigate to any appointment quickly by providing its ID below:
          </DialogDescription>
        </DialogHeader>
        <Stack align="stretch" gap={6}>
          <div className="relative">
            <Input
              className="pr-10"
              disabled={isLoading || isProvisioning}
              onChange={handleValueChange}
              placeholder="Appointment ID"
              value={appointmentId}
            />
            <div className="absolute top-0 right-0 size-10">
              {isLoading && <LoadingIndicator />}
            </div>
          </div>
          {errorMessage && (
            <Stack
              className="rounded-lg border border-border-negative-subtle bg-bg-negative-subtle p-3 text-text-negative-subtle"
              gap={3}
              direction="horizontal"
            >
              <Icon name="AlertCircle" />
              <Text color="inherit" weight="regular">
                {errorMessage}
              </Text>
            </Stack>
          )}
          {!errorMessage && tenantId && tenantName && (
            <Stack
              className="rounded-lg border border-border-warning-subtle bg-bg-warning-subtle p-3 text-text-warning-subtle"
              gap={3}
              direction="horizontal"
            >
              <Icon name="AlertTriangle" />
              <Stack align="stretch">
                <Text color="inherit" weight="regular">
                  You don't have access to this organization:
                </Text>
                <Text color="inherit">{tenantName}</Text>
              </Stack>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    disabled={hasReachedTenantLimit}
                    loading={isProvisioning}
                    onClick={handleProvision}
                  >
                    Provision
                  </Button>
                </TooltipTrigger>
                {hasReachedTenantLimit && (
                  <TooltipContent>
                    You reached the maximum number of provisioned tenants
                    allowed.
                  </TooltipContent>
                )}
              </Tooltip>
            </Stack>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
